import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, User } from "lucide-react";

const API_URL = '';

export default function CoordinatorLoginPage() {
  const navigate = useNavigate();
  const [coordinators, setCoordinators] = useState([]);
  const [selectedCoordinator, setSelectedCoordinator] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingCoordinators, setLoadingCoordinators] = useState(true);

  useEffect(() => {
    fetchCoordinators();
  }, []);

  const fetchCoordinators = async (retryCount = 0) => {
    try {
      const response = await axios.get(`${API_URL}/api/public/coordinators`);
      const coordList = response.data.coordinators || [];
      setCoordinators(coordList);
      setLoadingCoordinators(false);
      localStorage.setItem('cachedCoordinators', JSON.stringify(coordList));
    } catch (err) {
      console.error('Error fetching coordinators:', err);
      if (retryCount < 5) {
        setTimeout(() => fetchCoordinators(retryCount + 1), 2000);
      } else {
        const cached = localStorage.getItem('cachedCoordinators');
        if (cached) {
          try { setCoordinators(JSON.parse(cached)); } catch(e) {}
        }
        setLoadingCoordinators(false);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    if (!selectedCoordinator) {
      setError("Please select a coordinator");
      setLoading(false);
      return;
    }

    if (!password) {
      setError("Please enter your password");
      setLoading(false);
      return;
    }

    try {
      // Find coordinator email
      const coordinator = coordinators.find(c => c.id === selectedCoordinator);
      
      if (!coordinator) {
        setError("Selected coordinator not found");
        setLoading(false);
        return;
      }

      const response = await axios.post(`${API_URL}/api/auth/login`, {
        email: coordinator.email,
        password: password
      });
      
      // Check if user is a coordinator
      if (response.data.user.role !== "coordinator") {
        setError("This login is only for coordinators. Please use the correct login page.");
        setLoading(false);
        return;
      }

      // Store auth data - use 'authToken' for consistency with other pages
      localStorage.setItem("authToken", response.data.access_token);
      localStorage.setItem("token", response.data.access_token); // For backward compatibility
      localStorage.setItem("user", JSON.stringify(response.data.user));

      // Redirect to coordinator dashboard
      navigate("/coordinator/dashboard");
    } catch (err) {
      setError(
        err.response?.data?.detail || "Login failed. Please check your credentials."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Side - Penama Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-blue-600 via-blue-500 to-teal-500 p-12 flex-col justify-between relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-white rounded-full -translate-x-1/2 -translate-y-1/2"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-white rounded-full translate-x-1/2 translate-y-1/2"></div>
        </div>
        
        <div className="relative z-10">
          <div className="flex items-center space-x-3 mb-8">
            <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center">
              <span className="text-2xl font-bold text-blue-600">P</span>
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">Penama Education Portal</h1>
              <p className="text-blue-100 text-sm">Penama Province, Vanuatu</p>
            </div>
          </div>
        </div>

        <div className="relative z-10">
          <h2 className="text-4xl font-bold text-white mb-6">
            PENAMA EDUCATION PORTAL
          </h2>
          <p className="text-xl text-blue-50 mb-8">
            Empowering Education Across Penama Province
          </p>
          <div className="space-y-4">
            <div className="flex items-start space-x-3">
              <div className="w-6 h-6 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <div>
                <p className="text-white font-medium">Multi-School Oversight</p>
                <p className="text-blue-100 text-sm">Monitor multiple schools from one dashboard</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <div className="w-6 h-6 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <div>
                <p className="text-white font-medium">Provincial Analytics</p>
                <p className="text-blue-100 text-sm">Track performance across all schools</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <div className="w-6 h-6 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
              </div>
              <div>
                <p className="text-white font-medium">Coordinate Teachers</p>
                <p className="text-blue-100 text-sm">Support and manage teaching staff</p>
              </div>
            </div>
          </div>
        </div>

        <div className="relative z-10 text-blue-100 text-sm">
          <p>Serving 6 Pilot Schools across Penama Province</p>
        </div>
      </div>

      {/* Right Side - Login Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-gray-50">
        <div className="w-full max-w-md">
          <div className="mb-8 text-center lg:hidden">
            <div className="flex items-center justify-center space-x-3 mb-4">
              <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center">
                <span className="text-2xl font-bold text-white">P</span>
              </div>
              <div className="text-left">
                <h1 className="text-xl font-bold text-gray-900">Penama Education Portal</h1>
                <p className="text-gray-600 text-sm">Penama Province, Vanuatu</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-lg p-8">
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Coordinator Login</h2>
              <p className="text-gray-600">
                Welcome back! Enter your credentials to access your coordinator dashboard.
              </p>
            </div>

            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            {loadingCoordinators ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Coordinator Selection - Native select for Edge compatibility */}
                <div>
                  <Label htmlFor="coordinator" className="text-gray-700 font-medium">
                    <User className="w-4 h-4 inline mr-2" />
                    Select Coordinator
                  </Label>
                  <select
                    id="coordinator"
                    data-testid="coordinator-select"
                    value={selectedCoordinator}
                    onChange={(e) => setSelectedCoordinator(e.target.value)}
                    className="mt-1 flex h-12 w-full items-center rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <option value="">Choose your name...</option>
                    {coordinators.map((coordinator) => (
                      <option key={coordinator.id} value={coordinator.id}>
                        {coordinator.name}{coordinator.department ? ` - ${coordinator.department}` : ''}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Password Input */}
                <div>
                  <Label htmlFor="password" className="text-gray-700 font-medium">
                    Password
                  </Label>
                  <Input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your password"
                    className="mt-1 h-12"
                    disabled={!selectedCoordinator}
                  />
                </div>

                <Button
                  type="submit"
                  disabled={loading || !selectedCoordinator || !password}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                      Signing in...
                    </>
                  ) : (
                    'Sign In to Dashboard'
                  )}
                </Button>
              </form>
            )}

            <div className="mt-6 pt-6 border-t border-gray-200">
              <p className="text-sm text-gray-600 text-center">
                Available Accounts:
              </p>
              <div className="mt-2 space-y-1 text-xs text-gray-500 text-center">
                <p>• Primary Coordinator (4 primary schools)</p>
                <p>• Secondary Coordinator (2 secondary schools)</p>
              </div>
            </div>

            <div className="mt-6 text-center">
              <button
                onClick={() => navigate("/")}
                className="text-sm text-blue-600 hover:text-blue-700 font-medium"
              >
                ← Back to Home
              </button>
            </div>
          </div>

          <div className="mt-8 text-center text-sm text-gray-600">
            <p>Need help? Contact IT Support</p>
          </div>
        </div>
      </div>
    </div>
  );
}
